import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { connect } from "react-redux";
import MagicIcon from "../icons/MagicIcon";
import OptimizedTooltip from "../common/OptimizedTooltip";
import {
  reoptimizeRoutes,
  setChangedVehicles
} from "../../store/actions/project";
import SwapIcon from "@material-ui/icons/SwapVert";
import API from "../../utils/axios";
import { setLoadingRoutesList } from "../../store/actions/loading";
import { setSolution } from "../../store/actions/solution";
import { getCookie } from "../../utils/cookies";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import LockIcon from "@material-ui/icons/Lock";
import { setInfoDialog } from "../../store/actions/common";
import { handleRouteLock } from "../../store/actions/custom";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import { headerRow } from "../data/constants";
import {
  distanceStr,
  exportToXlsx,
  generateStopDataForXLSX,
  getDateValue,
  mergeReloadStopsToMainRoute
} from "../../utils/helpers";
import ReceiptIcon from "@material-ui/icons/Receipt";
import ReactToPrint from "react-to-print";
import PrintLabels from "../project/PrintLabels";
import PrintIcon from "@material-ui/icons/Print";
import PrintRoutes from "../project/PrintRoutes";
import ReplyIcon from "@material-ui/icons/Reply";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import DateFnsUtils from "@date-io/date-fns";
import { SortByAlpha } from "@material-ui/icons";

const getTimeWindowLatest = stop =>
  stop.time_windows && stop.time_windows.length
    ? stop.time_windows[0].latest
    : stop.time_window_latest ?? "";

const useStyles = makeStyles(theme => ({
  wrapper: {
    padding: theme.spacing(2, 3),
    borderStyle: "solid",
    borderColor: "#EAEBF0",
    borderWidth: 0,
    borderTopWidth: 1,
    boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.16)",
    height: 64,
    maxHeight: 64
  },
  iconButton: {
    "&.Mui-disabled": {
      opacity: 0.5
    }
  },

  driverLink: {
    "& svg": {
      transform: "rotateY(180deg)"
    }
  }
}));

const dateFns = new DateFnsUtils();

function RouteBottomActions(props) {
  const {
    backhaulGroup,
    reoptimizeRoutes,
    setLoadingRoutesList,
    solution,
    setChangedVehicles,
    projectId,
    setSolution,
    setInfoDialog,
    handleRouteLock,
    published,
    billingDetails,
    organisation,
    project,
    shareRoute,
    unassignedStopsCount,
    isReloadAllowed,
    distanceUnits,
    isPastProject
  } = props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Variables
  const mainRoute = backhaulGroup[0];
  const routesId = backhaulGroup.map(route => route.id);
  const allStops = backhaulGroup.map(routeGroup => routeGroup.stops);

  // States
  const [isPrintingLabel, setPrintingLabel] = useState(false);
  const [isPrinting, setPrinting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({});

  // Refs
  const printRef = useRef();
  const labelsPrintRef = useRef();
  let readyToPrint = useRef(false);
  let readyToPrintLabel = useRef(false);

  // Callbacks
  const swapRoutes = () => {
    setLoadingRoutesList(true);

    // Count the change
    routesId.forEach(routeId => {
      const route = solution.routes.find(route => route.id === routeId);

      route && setChangedVehicles(route.vehicle_id);
    });

    API.patch(`/v1/projects/${projectId}/routes?action=reverse`, routesId)
      .then(res => {
        const newSolution = { ...solution };
        newSolution.routes = newSolution.routes.map(route =>
          routesId.includes(route.id)
            ? res.data.routes.find(
                reversedRoute => reversedRoute.id === route.id
              )
            : route
        );
        setSolution(newSolution);
        enqueueSnackbar("Route reversed.");
      })
      .catch(error => console.log(error))
      .finally(() => setLoadingRoutesList(false));
  };

  const sortByTimeWindow = () => {
    setLoadingRoutesList(true);

    const routeId = routesId[0];
    const currentRoute = solution.routes.find(route => routeId === route.id);
    const routeStops = currentRoute.stops;

    const sortedRoute = routeStops.sort((a, b) => {
      const firstTimeWindowLatest = getTimeWindowLatest(a);
      const secondTimeWindowLatest = getTimeWindowLatest(b);

      if (firstTimeWindowLatest > secondTimeWindowLatest) {
        return 1;
      } else if (firstTimeWindowLatest < secondTimeWindowLatest) {
        return -1;
      } else {
        return 0;
      }
    });

    API.patch(
      `/v1/projects/${projectId}/routes/${routeId}`,
      sortedRoute.map(stop => stop.id)
    )
      .then(res => {
        const newSolution = { ...solution };
        console.log(res.data);
        newSolution.routes = newSolution.routes.map(route =>
          routesId.includes(route.id) ? res.data : route
        );
        setSolution(newSolution);
        enqueueSnackbar("Route sorted by time window.");
      })
      .catch(error => console.log(error))
      .finally(() => setLoadingRoutesList(false));
  };

  const handleDownloadRoutes = () => {
    const header = [...headerRow];
    header.unshift("route", "vehicle", "driver", "stop", "eta");
    isPastProject &&
      header.push(
        "project_name",
        "project_date",
        "route_started_at",
        "route_completed_at",
        "delivery_status",
        "delivered_at",
        "external_note",
        "internal_note",
        distanceUnits === "km"
          ? "planned_distance_km"
          : "planned_distance_miles",
        "pod_age",
        "pod_signature",
        "pod_photo"
      );
    const newRoutes = isReloadAllowed
      ? mergeReloadStopsToMainRoute(solution.routes)
      : solution.routes;

    const data = [
      header,
      ...newRoutes.reduce((array, route) => {
        let count = 0;
        return routesId.includes(route.id)
          ? [
              ...array,
              ...route.stops
                .filter(
                  task =>
                    !["start", "end", "break", "reload"].includes(task.type)
                )
                .reduce((array, row) => {
                  count++;
                  return [
                    ...array,
                    ...(!row.units || row.units.length === 0
                      ? [
                          generateStopDataForXLSX(row, 0, {
                            type: "routes",
                            isPastProject,
                            published,
                            route: route.index,
                            vehicle: route.vehicle_name,
                            driver: route.driver
                              ? `${route.driver.first_name} ${route.driver.last_name}`
                              : "",

                            stop: count,
                            project_name: project.name,
                            project_date: dateFns.format(
                              new Date(getDateValue(project.project_date)),
                              "dd-MM-yyyy"
                            ),
                            route_started_at: route.start_timestamp,
                            route_completed_at: route.end_timestamp,
                            planned_distance: distanceStr(
                              route.distances,
                              distanceUnits === "km",
                              true
                            )
                          })
                        ]
                      : row.units.map((unit, i) =>
                          generateStopDataForXLSX(row, i, {
                            type: "routes",
                            isPastProject,
                            published,
                            route: route.index,
                            vehicle: route.vehicle_name,
                            driver: route.driver
                              ? `${route.driver.first_name} ${route.driver.last_name}`
                              : "",

                            stop: count,
                            project_name: project.name,
                            project_date: dateFns.format(
                              new Date(getDateValue(project.project_date)),
                              "dd-MM-yyyy"
                            ),
                            route_started_at: route.start_timestamp,
                            route_completed_at: route.end_timestamp,
                            planned_distance: distanceStr(
                              route.distances,
                              distanceUnits === "km",
                              true
                            )
                          })
                        ))
                  ];
                }, [])
            ]
          : array;
      }, [])
    ];
    exportToXlsx("project_solution.xlsx", data);
  };

  // Effects
  useEffect(() => {
    if (isPrinting && !readyToPrint.current) {
      readyToPrint.current = true;
    } else if (readyToPrint.current) {
      readyToPrint.current = false;
    }
  }, [isPrinting]);

  useEffect(() => {
    if (isPrintingLabel && !readyToPrintLabel.current) {
      readyToPrintLabel.current = true;
    } else if (readyToPrintLabel.current) {
      readyToPrintLabel.current = false;
    }
  }, [isPrintingLabel]);

  return (
    <>
      <Grid
        container
        wrap="nowrap"
        alignItems="center"
        justify="flex-end"
        className={classes.wrapper}
      >
        {!published && (
          <>
            <Grid item>
              <OptimizedTooltip placement="bottom" title={"Reoptimize route"}>
                <span>
                  <IconButton
                    onClick={() => {
                      if (unassignedStopsCount > 0) {
                        setConfirmDialog({
                          visible: true,
                          type: "saveDisabled",
                          action: "reoptimize",
                          actionType: "reoptimize_unscheduled"
                        });
                      } else {
                        reoptimizeRoutes(routesId);
                      }
                    }}
                    className={classes.iconButton}
                    disabled={mainRoute.locked || allStops.length < 1}
                    color="primary"
                  >
                    <MagicIcon color="primary" />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            </Grid>
            {backhaulGroup.length === 1 && (
              <Grid item>
                <OptimizedTooltip placement="bottom" title={"Reverse route"}>
                  <span>
                    <IconButton
                      onClick={swapRoutes}
                      className={classes.iconButton}
                      disabled={mainRoute.locked || allStops.length < 1}
                      color="primary"
                    >
                      <SwapIcon />
                    </IconButton>
                  </span>
                </OptimizedTooltip>
              </Grid>
            )}
            <Grid item>
              <OptimizedTooltip
                placement="bottom"
                title={"Sort by time window"}
              >
                <span>
                  <IconButton
                    onClick={sortByTimeWindow}
                    className={classes.iconButton}
                    disabled={mainRoute.locked || allStops.length < 1}
                    color="primary"
                  >
                    <SortByAlpha />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            </Grid>
            <Divider
              flexItem
              orientation="vertical"
              style={{ margin: "0 4px " }}
            />
            <Grid item>
              <OptimizedTooltip
                placement="bottom"
                title={mainRoute.locked ? `Unlock route` : `Lock route`}
              >
                <IconButton
                  className={classes.iconButton}
                  onClick={() => {
                    if (mainRoute.locked || getCookie("dontShowLockDialog")) {
                      handleRouteLock(
                        mainRoute.id,
                        mainRoute.vehicle_id,
                        mainRoute.locked
                      );
                    } else {
                      setInfoDialog({
                        visible: true,
                        checkBox: true,
                        title: "Lock route",
                        body: "You can lock routes to prevent them from being changed. For example, if you click on the reoptimize button, all the remaining routes will be reoptimized without changing your locked routes.",
                        action: () =>
                          handleRouteLock(
                            mainRoute.id,
                            mainRoute.vehicle_id,
                            mainRoute.locked
                          )
                      });
                    }
                  }}
                >
                  {mainRoute.locked ? <LockOpenIcon /> : <LockIcon />}
                </IconButton>
              </OptimizedTooltip>
            </Grid>
            <Divider
              flexItem
              orientation="vertical"
              style={{ margin: "0 4px " }}
            />
          </>
        )}
        {project.published_once && (
          <Grid item>
            <OptimizedTooltip placement="bottom" title="Share route">
              <span>
                <IconButton
                  onClick={() => shareRoute(routesId)}
                  className={clsx(classes.iconButton, classes.driverLink)}
                >
                  <ReplyIcon />
                </IconButton>
              </span>
            </OptimizedTooltip>
          </Grid>
        )}
        <Grid item>
          <ReactToPrint
            key={0}
            onBeforeGetContent={() => {
              setPrintingLabel(true);
              setLoadingRoutesList(true);
              return new Promise((resolve, reject) => {
                const interval = setInterval(() => {
                  if (readyToPrintLabel.current) {
                    setLoadingRoutesList(false);
                    clearInterval(interval);
                    resolve();
                  }
                }, 500);
              });
            }}
            pageStyle={`
                    @media print {
                      body { 
                        -webkit-print-color-adjust: exact; 
                      }
                    }
                    @page { size: 100mm 150mm;  margin: 0; }
                  `}
            onAfterPrint={() => {
              setPrintingLabel(false);
            }}
            trigger={() => (
              <OptimizedTooltip
                placement="bottom"
                title={`Print shipping labels`}
              >
                <span>
                  <IconButton className={classes.iconButton}>
                    <ReceiptIcon />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            )}
            content={() => {
              return labelsPrintRef.current;
            }}
          />
        </Grid>
        <Grid item>
          <ReactToPrint
            key={1}
            onBeforeGetContent={() => {
              setPrinting(true);
              setLoadingRoutesList(true);
              return new Promise((resolve, reject) => {
                const interval = setInterval(() => {
                  if (readyToPrint.current) {
                    setLoadingRoutesList(false);
                    clearInterval(interval);
                    resolve();
                  }
                }, 500);
              });
            }}
            pageStyle={`
                @media print {
                  body { 
                    -webkit-print-color-adjust: exact; 
                  }
                }
                @page { size: auto;  margin: 16mm 16mm 8mm 16mm; }
              `}
            onAfterPrint={() => setPrinting(false)}
            trigger={() => (
              <OptimizedTooltip placement="bottom" title={`Print route`}>
                <span>
                  <IconButton className={classes.iconButton}>
                    <PrintIcon />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            )}
            content={() => {
              return printRef.current;
            }}
          />
        </Grid>
        <Grid item>
          <OptimizedTooltip placement="bottom" title="Download route">
            <span>
              <IconButton
                onClick={handleDownloadRoutes}
                className={classes.iconButton}
              >
                <SaveAltIcon />
              </IconButton>
            </span>
          </OptimizedTooltip>
        </Grid>
      </Grid>
      <Box display="none">
        <Box ref={printRef}>
          {solution && isPrinting && (
            <PrintRoutes
              routes={solution.routes}
              routeIds={routesId}
              projectName={project.name}
            />
          )}
        </Box>
      </Box>
      <Box display="none">
        <Box ref={labelsPrintRef}>
          {solution && isPrintingLabel && (
            <PrintLabels
              solution={solution}
              selectedRoutes={routesId}
              billingDetails={billingDetails}
              organisation={organisation}
            />
          )}
        </Box>
      </Box>
      {confirmDialog && (
        <ConfirmDialog
          open={confirmDialog.visible}
          handleClose={() =>
            setConfirmDialog({
              ...confirmDialog,
              visible: false
            })
          }
          action={(e, dialogState) => {
            if (confirmDialog?.actionType === "reoptimize_unscheduled") {
              dialogState.checked
                ? reoptimizeRoutes(routesId, {
                    optimize_unassigned_stops: true
                  })
                : reoptimizeRoutes(routesId, {
                    optimize_unassigned_stops: false
                  });
            }
          }}
          title="You have unscheduled stops in your project"
          body={
            <>
              <Typography variant="p">
                {`Do you want to try to plan ${unassignedStopsCount} unscheduled
                stop${unassignedStopsCount > 1 ? "s" : ""} in your routes?`}
              </Typography>
            </>
          }
          confirmText="Reoptimize"
          modalType={
            confirmDialog?.actionType === "reoptimize_unscheduled"
              ? "withCheckbox"
              : ""
          }
        />
      )}
    </>
  );
}

const mapStateToProps = state => ({
  solution: state.project.solution,
  projectId: state.project.id,
  published: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  billingDetails: state.account.billingDetails,
  organisation:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation) ||
    {},
  project: state.project,
  unassignedStopsCount: state.project.solution.unassigned.stops.length,
  isReloadAllowed: state.project.vrp.configuration.allow_reload,
  distanceUnits: state.project.vrp.configuration.distance_units,
  isPastProject: ["expired", "finished"].includes(
    state.project.status.toLowerCase()
  )
});

export default connect(mapStateToProps, {
  reoptimizeRoutes,
  setLoadingRoutesList,
  setChangedVehicles,
  setSolution,
  setInfoDialog,
  handleRouteLock
})(RouteBottomActions);
